import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  ArrowRight,
  Building2,
  Search,
  Bell,
  MessageSquare,
  Sparkles,
  Globe,
  Shield,
  Zap,
  Eye,
  Target,
  Clock,
  TrendingUp,
  CheckCircle,
  ChevronRight,
  BarChart3,
  Users,
  Microscope,
  Pill,
  FlaskConical,
  Network,
} from 'lucide-react';

// Platform capabilities
const platformCapabilities = [
  {
    icon: BarChart3,
    title: 'Market Intelligence',
    description: 'Real-time insights on biotech assets, pipelines, and deal activity across therapeutic areas.',
  },
  {
    icon: Target,
    title: 'AI-Powered Matching',
    description: 'Proprietary algorithms surface the most relevant opportunities based on your BD strategy.',
  },
  {
    icon: Eye,
    title: 'Intent Analytics',
    description: 'Track buyer interest signals and engagement patterns to optimize outreach timing.',
  },
  {
    icon: Network,
    title: 'Deal Network',
    description: 'Connect directly with verified pharma BD teams and biotech executives.',
  },
];

// Stats data
const platformStats = [
  { value: '500+', label: 'Biotech Companies' },
  { value: '50+', label: 'Pharma Partners' },
  { value: '$2B+', label: 'Deal Flow Tracked' },
  { value: '90%', label: 'Match Accuracy' },
];

// Therapeutic areas
const therapeuticAreas = [
  { name: 'Oncology', icon: Microscope },
  { name: 'Immunology', icon: Shield },
  { name: 'Neurology', icon: FlaskConical },
  { name: 'Rare Disease', icon: Pill },
];

export default function Landing() {
  const { isAuthenticated, userProfile } = useAuth();

  const getDashboardUrl = () => {
    // Check both role and user_type for admin
    const isAdmin = userProfile?.role === 'admin' || userProfile?.user_type === 'admin';
    const userType = isAdmin ? 'admin' : userProfile?.user_type;
    
    if (userType === 'admin') return '/admin/dashboard';
    if (userType === 'buyer') return '/buyer/dashboard';
    return '/seller/dashboard';
  };

  return (
    <div className="min-h-screen bg-[#0B1221]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B1221]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-lg">
                <img
                  src="/logo.png"
                  alt="Pharmunity"
                  className="h-12 w-12 object-cover scale-[1.35]"
                />
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">Pharmunity</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#platform" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Platform
              </a>
              <a href="#for-biotech" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                For Biotech
              </a>
              <a href="#for-pharma" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                For Pharma BD
              </a>
              <a href="#insights" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">
                Insights
              </a>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link
                  to={getDashboardUrl()}
                  className="bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-500 transition-colors font-medium text-sm"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-300 hover:text-white transition-colors font-medium text-sm px-4 py-2"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-500 transition-colors font-medium text-sm shadow-lg shadow-blue-600/20"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Orbs */}
          <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-purple-600/15 rounded-full blur-[100px]" />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600/10 border border-blue-500/20 rounded-full mb-8">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-blue-400 text-sm font-medium">Pharmaceutical Business Development Platform</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-purple-500">
                  BD Intelligence.
                </span>{' '}
                Deal Advantage.
              </h1>

              {/* Slogan */}
              <p className="text-xl lg:text-2xl text-gray-300 mb-6 font-medium">
                Know who's looking. Move before they call.
              </p>

              {/* Subheadline */}
              <p className="text-lg text-gray-500 mb-10 leading-relaxed max-w-xl">
                The buyer intent platform for pharma licensing.
                See which big pharma BD teams are researching your therapeutic area — before they reach out.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link
                  to="/register?type=seller"
                  className="group bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-base hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-3 shadow-xl shadow-black/20"
                >
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <span>List Your Assets</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/register?type=buyer"
                  className="group bg-blue-600/20 text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-blue-600/30 transition-all inline-flex items-center justify-center gap-3 border border-blue-500/30"
                >
                  <Search className="w-5 h-5" />
                  <span>Find Opportunities</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center gap-8 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>NDA Protected</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" />
                  <span>Global Network</span>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative hidden lg:block">
              {/* Dashboard Preview Card */}
              <div className="relative bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-medium">Interest Analytics</span>
                  </div>
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Live</span>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: 'Profile Views', value: '127', change: '+24%' },
                      { label: 'Engaged Buyers', value: '18', change: '+8' },
                      { label: 'Match Score', value: '92%', change: 'High' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white/5 rounded-lg p-3">
                        <div className="text-2xl font-bold text-white">{stat.value}</div>
                        <div className="text-xs text-gray-500">{stat.label}</div>
                        <div className="text-xs text-green-400 mt-1">{stat.change}</div>
                      </div>
                    ))}
                  </div>

                  {/* Interest List */}
                  <div className="space-y-3">
                    {[
                      { company: 'Pfizer BD Team', views: 12, time: '2h ago', hot: true },
                      { company: 'Roche Partnering', views: 8, time: 'Yesterday', hot: true },
                      { company: 'Novartis Strategy', views: 5, time: '3 days ago', hot: false },
                    ].map((item) => (
                      <div key={item.company} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center font-bold text-white">
                            {item.company[0]}
                          </div>
                          <div>
                            <div className="font-medium text-white text-sm">{item.company}</div>
                            <div className="text-xs text-gray-500">{item.views} views · {item.time}</div>
                          </div>
                        </div>
                        {item.hot && (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            Hot
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-xl border border-white/10 p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">New Match!</div>
                    <div className="text-xs text-gray-500">92% alignment score</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0B1221]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities */}
      <section id="platform" className="py-24 bg-[#0F1724] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600/10 border border-blue-500/20 rounded-full mb-6">
              <span className="text-blue-400 text-sm font-medium">Platform Capabilities</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Built for Modern Pharma BD
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comprehensive tools and intelligence to accelerate deal discovery, evaluation, and execution.
            </p>
          </div>

          {/* Capabilities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformCapabilities.map((capability) => (
              <div
                key={capability.title}
                className="group bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-2xl p-6 border border-white/5 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600/20 transition-colors">
                  <capability.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{capability.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Biotech Section */}
      <section id="for-biotech" className="py-24 bg-[#0B1221] relative overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-600/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-600/10 border border-purple-500/20 rounded-full mb-6">
                <Building2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">For Biotech Companies</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Get Discovered by the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Right Partners
                </span>
              </h2>

              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Stop pitching into the void. See which pharma BD teams are actively researching your
                therapeutic area and reach out when interest is highest.
              </p>

              {/* Benefits */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Eye, text: 'Real-time visibility into buyer engagement' },
                  { icon: Bell, text: 'Instant alerts when high-intent buyers view your profile' },
                  { icon: Target, text: 'AI matching with strategically aligned partners' },
                  { icon: MessageSquare, text: 'Direct messaging with verified BD teams' },
                ].map((benefit) => (
                  <div key={benefit.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="text-gray-300">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/register?type=seller"
                className="group inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-purple-500 transition-colors"
              >
                Get Discovered
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-2xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Eye className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-medium">Who's Interested</span>
                </div>

                {/* Content */}
                <div className="p-6 space-y-3">
                  {[
                    { company: 'Pfizer Oncology BD', interest: 'High', stage: 'Phase 2 Assets', time: '2h ago' },
                    { company: 'Roche Partnering', interest: 'High', stage: 'ADC Platform', time: '5h ago' },
                    { company: 'Novartis Strategy', interest: 'Medium', stage: 'Early Pipeline', time: 'Yesterday' },
                    { company: 'Merck BD Team', interest: 'Medium', stage: 'Immunology', time: '2 days ago' },
                  ].map((item, index) => (
                    <div key={item.company} className={`flex items-center justify-between p-4 rounded-xl ${index === 0 ? 'bg-purple-600/10 border border-purple-500/20' : 'bg-white/5'}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg flex items-center justify-center font-bold text-white text-sm">
                          {item.company.split(' ')[0][0]}
                        </div>
                        <div>
                          <div className="font-medium text-white">{item.company}</div>
                          <div className="text-xs text-gray-500">{item.stage} · {item.time}</div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.interest === 'High' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {item.interest}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Pharma BD Section */}
      <section id="for-pharma" className="py-24 bg-[#0F1724] relative overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-blue-600/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Visual - Left on desktop */}
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-2xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-medium">AI-Powered Matches</span>
                </div>

                {/* Search Filters */}
                <div className="px-6 py-4 border-b border-white/10">
                  <div className="flex flex-wrap gap-2">
                    {['Oncology', 'ADC', 'Phase 1-2', 'US/EU'].map((filter) => (
                      <span key={filter} className="px-3 py-1.5 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                        {filter}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Results */}
                <div className="p-6 space-y-3">
                  {[
                    { name: 'Oncology Innovations Inc', match: 94, stage: 'Phase 2', modality: 'HER2 ADC' },
                    { name: 'BioTarget Therapeutics', match: 89, stage: 'Phase 1', modality: 'Bispecific' },
                    { name: 'Novel Antibody Co', match: 85, stage: 'Preclinical', modality: 'ADC Platform' },
                  ].map((item, index) => (
                    <div key={item.name} className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      index === 0 ? 'bg-blue-600/10 border-blue-500/30' : 'bg-white/5 border-transparent hover:border-white/10'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{item.name}</span>
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                          {item.match}% Match
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.stage} · {item.modality}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content - Right on desktop */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600/10 border border-blue-500/20 rounded-full mb-6">
                <Search className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">For Pharma BD Teams</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Find Assets{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  Before Conference Season
                </span>
              </h2>

              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Skip the conference chaos. Discover promising biotech assets that match your strategy
                with AI-powered search and real-time pipeline intelligence.
              </p>

              {/* Benefits */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Search, text: 'Advanced search by TA, modality, stage, and geography' },
                  { icon: Sparkles, text: 'AI recommendations based on your portfolio strategy' },
                  { icon: Clock, text: 'Early access to pre-conference opportunities' },
                  { icon: BarChart3, text: 'Competitive intelligence and deal tracking' },
                ].map((benefit) => (
                  <div key={benefit.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-gray-300">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/register?type=buyer"
                className="group inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-500 transition-colors"
              >
                Start Discovering
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Therapeutic Areas */}
      <section id="insights" className="py-24 bg-[#0B1221]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-600/10 border border-blue-500/20 rounded-full mb-6">
              <span className="text-blue-400 text-sm font-medium">Coverage Areas</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Deep Therapeutic Expertise
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comprehensive coverage across high-value therapeutic areas with specialized intelligence.
            </p>
          </div>

          {/* Areas Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {therapeuticAreas.map((area) => (
              <div
                key={area.name}
                className="group bg-gradient-to-br from-[#151F2E] to-[#0F1724] rounded-2xl p-8 border border-white/5 hover:border-blue-500/30 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-blue-600/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600/20 transition-colors">
                  <area.icon className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{area.name}</h3>
                <p className="text-gray-500 text-sm">Explore opportunities</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-[#0F1724]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Get Started in Minutes
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Simple onboarding. Powerful results. Start discovering opportunities today.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Create Profile', desc: 'Set up your company profile with pipeline details or BD criteria' },
              { step: '02', title: 'Get Matched', desc: 'Our AI surfaces the most relevant opportunities for you' },
              { step: '03', title: 'Connect', desc: 'Message directly when interest is mutual' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-400">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '32px 32px'
            }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your BD Process?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join the platform where pharma and biotech connect with intent, not guesswork.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?type=seller"
              className="group bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-3 shadow-xl"
            >
              <Building2 className="w-5 h-5 text-blue-600" />
              List Your Assets
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/register?type=buyer"
              className="group bg-white/10 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 transition-all border border-white/20 inline-flex items-center justify-center gap-3"
            >
              <Search className="w-5 h-5" />
              Find Opportunities
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#0B1221] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden">
                <img
                  src="/logo.png"
                  alt="Pharmunity"
                  className="h-12 w-12 object-cover scale-[1.35]"
                />
              </div>
              <span className="text-2xl font-bold text-white">Pharmunity</span>
            </div>

            {/* Links */}
            <div className="flex items-center gap-8 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              <a href="mailto:admin@pharmunity.io" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-sm">
              © 2025 Pharmunity. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
